import { Controller } from "@hotwired/stimulus"

// Dynamically loads the model list from the selected provider.
//
// When the provider <select> changes, this controller:
//   1. Shows a loading state on the model field wrapper.
//   2. GETs /llm/providers/:id/models (JSON).
//   3. Replaces the model field with a <select> populated from the response.
//   4. Falls back to a plain text input on error.
//
// Targets:
//   providerSelect — the <select> for llm_provider_id
//   modelWrapper   — the container div that holds the model field
//   modelField     — the current model text_field (replaced on first load)
//
// Values:
//   modelsUrlTemplate — URL template with __ID__ as the provider id placeholder
//                       e.g. "/llm/providers/__ID__/models"
//   currentModel      — the model value already persisted on the record (for edit)

export default class extends Controller {
  static targets = ["providerSelect", "modelWrapper", "modelField"]
  static values  = {
    modelsUrlTemplate: String,
    currentModel:      String
  }

  connect() {
    // If a provider is already selected when the form loads (edit form),
    // immediately fetch the model list so the select is pre-populated.
    const providerId = this.providerSelectTarget.value
    if (providerId) {
      this.#fetchModels(providerId)
    }
  }

  // Called by data-action="change->routing-rule-model#providerChanged"
  providerChanged() {
    const providerId = this.providerSelectTarget.value

    if (!providerId) {
      this.#renderTextField(this.currentModelValue)
      return
    }

    this.#fetchModels(providerId)
  }

  // ── private ──────────────────────────────────────────────────────────────

  async #fetchModels(providerId) {
    this.#renderLoading()

    const url = this.modelsUrlTemplateValue.replace("__ID__", providerId)

    try {
      const response = await fetch(url, {
        headers: {
          "Accept":       "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data   = await response.json()
      const models = Array.isArray(data.models) ? data.models : []

      if (models.length === 0) {
        this.#renderTextField(this.currentModelValue)
        return
      }

      this.#renderModelSelect(models)
    } catch (_error) {
      this.#renderTextField(this.currentModelValue)
    }
  }

  #renderLoading() {
    this.modelWrapperTarget.innerHTML = `
      <p class="text-sm text-gray-500 py-1.5 animate-pulse">Loading models…</p>
    `
  }

  #renderModelSelect(models) {
    const current = this.currentModelValue

    const options = models
      .map(m => {
        const selected = m === current ? " selected" : ""
        return `<option value="${this.#escapeHtml(m)}"${selected}>${this.#escapeHtml(m)}</option>`
      })
      .join("")

    this.modelWrapperTarget.innerHTML = `
      <div class="grid grid-cols-1">
        <select
          name="llm_routing_rule[model]"
          id="llm_routing_rule_model"
          data-routing-rule-model-target="modelField"
          class="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
        >
          ${options}
        </select>
        <svg class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4"
             viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd"
                d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z"
                clip-rule="evenodd" />
        </svg>
      </div>
    `

    // Keep currentModelValue in sync with the select so that switching
    // providers and switching back restores the right selection.
    const select = this.modelWrapperTarget.querySelector("select")
    if (select) {
      select.addEventListener("change", () => {
        this.currentModelValue = select.value
      })
    }
  }

  #renderTextField(value = "") {
    this.modelWrapperTarget.innerHTML = `
      <input
        type="text"
        name="llm_routing_rule[model]"
        id="llm_routing_rule_model"
        value="${this.#escapeHtml(value)}"
        placeholder="e.g. llama3:latest"
        data-routing-rule-model-target="modelField"
        class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
      />
    `
  }

  #escapeHtml(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }
}
